import { Editor } from "@tinymce/tinymce-react";
import { EDITOR_KEY } from "../../../config/app";
import { Exercise, LessonType } from "../../../Types/Courses";
import Button from "../../../UI/Button/Button";
import ButtonLoading from "../../../UI/Button/ButtonLoading";
import InputField from "../../../UI/Form/InputField";
import ExerciseLinker from "./ExerciseLinker";
import { LessonFormState } from "./courseBuilder.types";

interface LessonEditorPanelProps {
  selectedUnitId: number | null;
  lessonForm: LessonFormState;
  savingLesson: boolean;
  exercises: Exercise[];
  loadingExercises: boolean;
  canLoadMoreExercises: boolean;
  onLessonFormChange: (patch: Partial<LessonFormState>) => void;
  onSearchExercise: (value: string) => void;
  onLoadMoreExercises: () => void;
  onOpenCreateExerciseModal: () => void;
  onGoToExerciseDetails: () => void;
  onReset: () => void;
  onCancel: () => void;
  onSave: () => void;
}

export default function LessonEditorPanel(props: LessonEditorPanelProps) {
  const {
    selectedUnitId,
    lessonForm,
    savingLesson,
    exercises,
    loadingExercises,
    canLoadMoreExercises,
    onLessonFormChange,
    onSearchExercise,
    onLoadMoreExercises,
    onOpenCreateExerciseModal,
    onGoToExerciseDetails,
    onReset,
    onCancel,
    onSave,
  } = props;

  const isExercise = lessonForm.type === "exercise";
  const showVideoUrl = lessonForm.type === "video" || lessonForm.type === "mixed";
  const showContent = lessonForm.type === "text" || lessonForm.type === "mixed";

  return (
    <section className="bg-white rounded-xl shadow border border-gray-100 p-4 md:p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Édition de leçon</h3>
          <p className="text-xs text-gray-500">
            Les champs changent selon le type de leçon sélectionné.
          </p>
        </div>
        <Button onClick={onReset}>Nouvelle</Button>
      </div>

      {!selectedUnitId ? (
        <p className="text-sm text-gray-500">
          Sélectionne une unité pour créer ou modifier une leçon.
        </p>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Titre</label>
            <InputField
              value={lessonForm.title}
              onChange={(event) => onLessonFormChange({ title: event.target.value })}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Type</label>
            <select
              className="w-full form-select"
              value={lessonForm.type}
              onChange={(event) => {
                const nextType = event.target.value as LessonType;
                onLessonFormChange({
                  type: nextType,
                  exercise_id: nextType === "exercise" ? lessonForm.exercise_id : "",
                });
              }}
            >
              <option value="text">Texte</option>
              <option value="video">Vidéo</option>
              <option value="mixed">Mixte</option>
              <option value="exercise">Exercice</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">Durée (minutes)</label>
            <InputField
              type="number"
              min={0}
              value={lessonForm.duration_minutes}
              onChange={(event) =>
                onLessonFormChange({
                  duration_minutes: event.target.value ? Number(event.target.value) : "",
                })
              }
            />
          </div>

          {isExercise ? (
            <ExerciseLinker
              exercises={exercises}
              selectedExerciseId={lessonForm.exercise_id}
              loading={loadingExercises}
              canLoadMore={canLoadMoreExercises}
              onSearchChange={onSearchExercise}
              onSelectExercise={(exerciseId) => onLessonFormChange({ exercise_id: exerciseId })}
              onOpenCreateModal={onOpenCreateExerciseModal}
              onLoadMore={onLoadMoreExercises}
              onGoToDetails={onGoToExerciseDetails}
            />
          ) : null}

          {showVideoUrl ? (
            <div>
              <label className="text-sm font-medium">URL vidéo</label>
              <InputField
                value={lessonForm.video_url}
                onChange={(event) => onLessonFormChange({ video_url: event.target.value })}
              />
            </div>
          ) : null}

          {showContent ? (
            <div>
              <label className="text-sm font-medium">Contenu</label>
              <Editor
                apiKey={EDITOR_KEY}
                value={lessonForm.content}
                onEditorChange={(value) => onLessonFormChange({ content: value })}
                init={{
                  height: 260,
                  menubar: false,
                  branding: false,
                  plugins:
                    "preview paste searchreplace autolink visualblocks fullscreen image link media table lists code",
                  toolbar:
                    "formatselect | bold italic underline | alignleft aligncenter alignright | bullist numlist | link image media | code",
                }}
              />
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={onCancel}>Annuler</Button>
            <ButtonLoading
              loading={savingLesson}
              label={lessonForm.id ? "Mettre à jour la leçon" : "Créer la leçon"}
              onClick={onSave}
            />
          </div>
        </div>
      )}
    </section>
  );
}
